import OKXAPI from './okxApi.js';

// Function to fetch current market prices for currencies
async function fetchMarketPrices(currencies) {
  if (!currencies || currencies.length === 0) {
    return {};
  }

  try {
    // Get market tickers for spot pairs
    const response = await fetch('https://www.okx.com/api/v5/market/tickers?instType=SPOT');
    const data = await response.json();
    
    if (data.code === '0' && data.data) {
      // Create a mapping of currency to price
      const priceMap = {};
      data.data.forEach(ticker => {
        // Extract base currency from instrument ID (e.g., BTC-USDT -> BTC)
        const parts = ticker.instId.split('-');
        const baseCcy = parts[0];
        const quoteCcy = parts[1];
        if (currencies.includes(baseCcy) && quoteCcy === 'USDT') {
          priceMap[baseCcy] = parseFloat(ticker.last);
        }
      });
      
      // If some currencies weren't found with USDT pairs, try with other stablecoins
      currencies.forEach(ccy => {
        if (!priceMap[ccy]) {
          // Look for pairs with other stablecoins like BUSD
          const tickerWithStablecoin = data.data.find(ticker => 
            ticker.instId.startsWith(`${ccy}-`) && 
            (ticker.instId.endsWith('USDT') || ticker.instId.endsWith('BUSD') || ticker.instId.endsWith('USDC'))
          );
          if (tickerWithStablecoin) {
            priceMap[ccy] = parseFloat(tickerWithStablecoin.last);
          }
        }
      });
      
      return priceMap;
    }
    return {};
  } catch (error) {
    console.error('Error fetching market prices:', error);
    return {};
  }
}

// Function to fetch savings products only
async function getOKXSavings(okxClient) {
  try {
    // Fetch savings balance if available
    const savingsResponse = await okxClient.getSavingsBalance();
    const savingsData = savingsResponse.data || [];

    // Process savings
    const savingsAssets = [];
    for (const saving of savingsData) {
      if (saving.amt && parseFloat(saving.amt) > 0) {
        savingsAssets.push({
          ccy: saving.ccy,
          symbol: saving.ccy,
          amount: parseFloat(saving.amt),
          value: 0, // Will be calculated with market prices
          price: 0, // Will be updated with market prices
          source: 'okx-savings',
          type: 'savings',
          savingId: saving.savingId,
          interestRate: saving.rate,
          accruedInterest: saving.accruedInterest
        });
      }
    }

    // Fetch market prices for savings to calculate values
    const currencies = [...new Set(savingsAssets.map(asset => asset.ccy))];
    const priceMap = await fetchMarketPrices(currencies);

    // Update values with market prices
    return savingsAssets.map(asset => {
      const marketPrice = priceMap[asset.ccy];
      const calculatedValue = marketPrice ? marketPrice * asset.amount : asset.value;
      return {
        ...asset,
        price: marketPrice,
        value: calculatedValue
      };
    });

  } catch (error) {
    console.error('Error fetching OKX savings:', error);
    return []; // Return empty array instead of throwing error
  }
}

// Mock function to simulate OKX API calls during development
// In production, replace with real API calls
export const getOKXData = async ({ apiKey, secretKey, passphrase }) => {
  try {
    // Initialize OKX API client
    const okxClient = new OKXAPI(apiKey, secretKey, passphrase);

    // Fetch account balance
    const balanceResponse = await okxClient.getAccountBalance();
    const balanceData = balanceResponse.data || [];

    // Fetch positions
    const positionsResponse = await okxClient.getPositions();
    const positionsData = positionsResponse.data || [];

    // Extract currencies from balances to fetch market prices
    const currencies = [];
    if (balanceData.length > 0) {
      const balanceDetails = balanceData[0].details || [];
      for (const detail of balanceDetails) {
        if (!currencies.includes(detail.ccy) && parseFloat(detail.availEq || detail.eq || 0) > 0) {
          currencies.push(detail.ccy);
        }
      }
    }

    // Add currencies from positions
    for (const position of positionsData) {
      if (Math.abs(parseFloat(position.pos || 0)) > 0) {
        const [baseCcy] = position.instId.split('-');
        if (!currencies.includes(baseCcy)) {
          currencies.push(baseCcy);
        }
      }
    }

    // Fetch market prices for the currencies
    const priceMap = await fetchMarketPrices(currencies);

    // Process balance data
    const processedBalances = [];
    if (balanceData.length > 0) {
      const balanceDetails = balanceData[0].details || [];
      for (const detail of balanceDetails) {
        if (parseFloat(detail.availEq || detail.eq || 0) > 0) {
          // Calculate value using market price if available, otherwise use OKX's equity value
          const marketPrice = priceMap[detail.ccy];
          const amount = parseFloat(detail.availEq || detail.eq || 0);
          const calculatedValue = marketPrice ? marketPrice * amount : parseFloat(detail.disEq || detail.eqUsd || 0);
          
          processedBalances.push({
            ccy: detail.ccy,
            symbol: detail.ccy,
            cashBal: parseFloat(detail.cashBal || detail.bal || 0),
            availBal: parseFloat(detail.availBal || detail.availEq || 0),
            frozenBal: parseFloat(detail.frozenBal || 0),
            totalEq: parseFloat(detail.eq || 0),
            disEq: parseFloat(detail.disEq || detail.eqUsd || 0),
            amount: amount,
            value: calculatedValue,
            price: marketPrice,
            source: 'okx',
            type: 'spot'
          });
        }
      }
    }

    // Process positions data
    const processedPositions = [];
    for (const position of positionsData) {
      if (Math.abs(parseFloat(position.pos || 0)) > 0) {
        // Extract base currency from instrument ID for price lookup
        const parts = position.instId.split('-');
        const baseCcy = parts[0];
        const marketPrice = priceMap[baseCcy];
        
        processedPositions.push({
          symbol: position.instId.replace('-', ''),
          instrumentId: position.instId,
          pos: parseFloat(position.pos || 0),
          avgPx: parseFloat(position.avgPx || 0),
          upl: parseFloat(position.upl || 0),
          uplRatio: parseFloat(position.uplRatio || 0),
          lever: parseFloat(position.lever || 1),
          mgnRatio: parseFloat(position.mgnRatio || 0),
          imr: parseFloat(position.imr || 0),
          mmr: parseFloat(position.mmr || 0),
          notionalUsd: parseFloat(position.notionalUsd || 0),
          source: 'okx',
          type: position.instType.toLowerCase(),
          posSide: position.posSide,
          price: marketPrice,
          value: marketPrice ? Math.abs(parseFloat(position.pos || 0)) * marketPrice : parseFloat(position.notionalUsd || 0)
        });
      }
    }

    // Process savings data
    const savingsData = await getOKXSavings(okxClient);
    const processedSavings = savingsData.map(saving => ({
      ...saving,
      value: saving.value,
      price: saving.price,
      source: saving.source,
      type: saving.type
    }));

    // Process open orders data
    const openOrdersData = await getOKXOpenOrders({ apiKey, secretKey, passphrase });
    const processedOpenOrders = openOrdersData.map(order => ({
      ...order,
      source: order.source,
      type: order.type
    }));

    // Combine all data
    const allAssets = [...processedBalances, ...processedPositions, ...processedSavings, ...processedOpenOrders];

    return allAssets;

  } catch (error) {
    console.error('Error fetching OKX data:', error);
    
    // Return mock data in case of error (for development purposes)
    return [
      {
        symbol: 'BTC',
        amount: 0.5,
        value: 32500,
        price: 65000,
        source: 'okx',
        type: 'spot'
      },
      {
        symbol: 'ETH',
        amount: 3.2,
        value: 12800,
        price: 4000,
        source: 'okx',
        type: 'spot'
      },
      {
        symbol: 'BNB',
        amount: 15,
        value: 4800,
        price: 320,
        source: 'okx',
        type: 'spot'
      }
    ];
  }
};

// Specific function to get OKX balances only
export const getOKXBalances = async ({ apiKey, secretKey, passphrase }) => {
  try {
    const okxClient = new OKXAPI(apiKey, secretKey, passphrase);
    const response = await okxClient.getAccountBalance();
    
    const balanceData = response.data || [];
    const assets = [];
    
    // Extract currencies from balances to fetch market prices
    const currencies = [];
    if (balanceData.length > 0) {
      const details = balanceData[0].details || [];
      for (const detail of details) {
        if (!currencies.includes(detail.ccy) && parseFloat(detail.eq || 0) > 0) {
          currencies.push(detail.ccy);
        }
      }
    }
    
    // Fetch market prices for the currencies
    const priceMap = await fetchMarketPrices(currencies);
    
    if (balanceData.length > 0) {
      const details = balanceData[0].details || [];
      for (const detail of details) {
        if (parseFloat(detail.eq || 0) > 0) {
          // Calculate value using market price if available, otherwise use OKX's equity value
          const marketPrice = priceMap[detail.ccy];
          const amount = parseFloat(detail.cashBal || detail.eq || 0);
          const calculatedValue = marketPrice ? marketPrice * amount : parseFloat(detail.disEq || detail.eqUsd || 0);
          
          assets.push({
            ccy: detail.ccy,
            symbol: detail.ccy,
            cashBal: parseFloat(detail.cashBal || detail.bal || 0),
            availBal: parseFloat(detail.availBal || 0),
            frozenBal: parseFloat(detail.frozenBal || 0),
            totalEq: parseFloat(detail.eq || 0),
            disEq: parseFloat(detail.disEq || detail.eqUsd || 0),
            amount: amount,
            value: calculatedValue,
            price: marketPrice,
            available: parseFloat(detail.availEq || detail.availBal || 0),
            frozen: parseFloat(detail.frozenBal || 0),
            source: 'okx-balance',
            type: 'spot'
          });
        }
      }
    }
    
    return assets;
  } catch (error) {
    console.error('Error fetching OKX balances:', error);
    return [];
  }
};

// Specific function to get OKX positions only
export const getOKXPositions = async ({ apiKey, secretKey, passphrase }) => {
  try {
    const okxClient = new OKXAPI(apiKey, secretKey, passphrase);
    const response = await okxClient.getPositions();
    
    const positionData = response.data || [];
    const positions = [];
    
    // Extract currencies from positions to fetch market prices
    const currencies = [];
    for (const position of positionData) {
      if (Math.abs(parseFloat(position.pos || 0)) > 0) {
        const [baseCcy] = position.instId.split('-');
        if (!currencies.includes(baseCcy)) {
          currencies.push(baseCcy);
        }
      }
    }
    
    // Fetch market prices for the currencies
    const priceMap = await fetchMarketPrices(currencies);
    
    for (const position of positionData) {
      if (Math.abs(parseFloat(position.pos || 0)) > 0) {
        // Extract base currency from instrument ID for price lookup
        const [baseCcy] = position.instId.split('-');
        const marketPrice = priceMap[baseCcy];
        
        positions.push({
          symbol: position.instId,
          instrumentId: position.instId,
          pos: parseFloat(position.pos || 0),
          avgPx: parseFloat(position.avgPx || 0),
          upl: parseFloat(position.upl || 0),
          uplRatio: parseFloat(position.uplRatio || 0),
          lever: parseFloat(position.lever || 1),
          mgnRatio: parseFloat(position.mgnRatio || 0),
          imr: parseFloat(position.imr || 0),
          mmr: parseFloat(position.mmr || 0),
          notionalUsd: parseFloat(position.notionalUsd || 0),
          source: 'okx-position',
          type: position.instType.toLowerCase(),
          posSide: position.posSide,
          price: marketPrice
        });
      }
    }
    
    return positions;
  } catch (error) {
    console.error('Error fetching OKX positions:', error);
    return [];
  }
};

// Specific function to get OKX savings only
export const getOKXSavingsOnly = async ({ apiKey, secretKey, passphrase }) => {
  try {
    const okxClient = new OKXAPI(apiKey, secretKey, passphrase);
    return await getOKXSavings(okxClient);
  } catch (error) {
    console.error('Error fetching OKX savings:', error);
    return []; // Return empty array instead of throwing to prevent app crashes
  }
};

// Specific function to get OKX open orders only
export const getOKXOpenOrders = async ({ apiKey, secretKey, passphrase }) => {
  try {
    const okxClient = new OKXAPI(apiKey, secretKey, passphrase);
    const response = await okxClient.getOpenOrders();
    
    if (response.code === '0' && response.data) {
      // Process open orders data
      const openOrders = response.data.map(order => {
        return {
          orderId: order.ordId,
          instrumentId: order.instId,
          tradeSide: order.side, // buy or sell
          orderType: order.ordType, // market, limit, post_only, etc.
          size: parseFloat(order.sz), // Order size
          filledSize: parseFloat(order.filledSz || 0), // Filled size
          price: parseFloat(order.px || 0), // Order price (0 for market orders)
          state: order.state, // live, partially_filled, etc.
          createTime: order.cTime, // Creation time
          updateTime: order.uTime, // Update time
          source: 'okx-open-order',
          type: 'order'
        };
      });
      
      return openOrders;
    }
    
    return [];
  } catch (error) {
    console.error('Error fetching OKX open orders:', error);
    return []; // Return empty array instead of throwing to prevent app crashes
  }
};